// The Temp tab: "now" cards for the five sensors (433 MHz Väljas / Kuur / Tuba, uHoo Kabinet / Magamistuba)
// with trend, today's range, humidity where the sensor has it and a 24 h sparkline, then the History card: all
// sensors on one chart (temperature + humidity panes), chips as legend / readout / toggle, and range statistics.
// Data: /data/{outside,kuur,tuba}_temperature<range>.json and uhoo_<range>.json (scripts/export.sh), the
// uHoo "now" from /energy/plan. Every file is optional: what's missing is hidden.
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';

import { EnergyAirQualityRoom, EnergyPlanSnapshot } from '../../common';
import { LayoutSize, layoutFor, useInterval, useViewportWidth } from '../util';
import { capitalize, formatAge, hhmm, num1, weekdayTime } from '../util/format';
import { HistoryRange, degrees, longRange, rangeInfo } from '../util/history';
import { HomeData, useHomeData } from '../util/home_data';
import { useHeaderStatus } from '../util/header_status';
import { Point, RrdFile, change, hasValues, latest, rangeStats, rrdColumn, rrdStepMs, valueAt } from '../util/rrd';
import { localMidnight } from '../util/tallinn_time';
import { HistoryCard, HistoryPane, HistorySeries, fittedAxis } from './history_card';
import { Card, Dot, LineMark, pageStyle } from './ui/card';
import { ToggleChip } from './ui/controls';
import { HeaderStatus } from './ui/header_status';
import { Sparkline } from './ui/sparkline';
import { DOWN, FAINT, INK_SOFT, MUTED, ROW_LINE, SECONDARY, UP, ZERO_LINE, HAIRLINE } from './ui/tokens';

const H = 3_600_000;
const SENSORS_STALE_AFTER_MS = 15 * 60_000;

type SensorId = 'outside' | 'kuur' | 'tuba' | 'Kabinet' | 'Magamistuba';

const SENSORS: { id: SensorId; name: string; src: string; color: string; uhoo: boolean }[] = [
  { id: 'outside', name: 'Väljas', src: 'outdoor', color: '#2a78d6', uhoo: false },
  { id: 'kuur', name: 'Kuur', src: 'shed', color: '#c98a00', uhoo: false },
  { id: 'tuba', name: 'Tuba', src: 'living', color: '#eb6834', uhoo: false },
  { id: 'Kabinet', name: 'Kabinet', src: 'uHoo', color: '#6250d6', uhoo: true },
  { id: 'Magamistuba', name: 'Magamistuba', src: 'uHoo', color: '#0f8c8c', uhoo: true },
];

// ---- Data -----------------------------------------------------------------------------------------------

interface SensorSeries {
  temp?: Point[];
  rh?: Point[];
  stepMs: number;
}
type RangeData = Partial<Record<SensorId, SensorSeries>>;

function sensorSeries(file: RrdFile | undefined, temp: string, rh?: string): SensorSeries | undefined {
  const t = rrdColumn(file, temp);
  const h = rh ? rrdColumn(file, rh) : undefined;
  if (!hasValues(t)) {
    return undefined;
  }
  return { temp: t, rh: hasValues(h) ? h : undefined, stepMs: rrdStepMs(file) ?? 300_000 };
}

async function loadRange(data: HomeData, range: HistoryRange): Promise<RangeData> {
  const [outside, kuur, tuba, uhoo] = await Promise.all([
    data.rrd(`outside_temperature${range}`),
    data.rrd(`kuur_temperature${range}`),
    data.rrd(`tuba_temperature${range}`),
    data.rrd(`uhoo_${range}`),
  ]);
  return {
    outside: sensorSeries(outside, 'Temperatuur', 'Õhuniiskus'),
    kuur: sensorSeries(kuur, 'Temperatuur'),
    tuba: sensorSeries(tuba, 'Temperatuur', 'Õhuniiskus'),
    Kabinet: sensorSeries(uhoo, 'Kabinet temp', 'Kabinet RH'),
    Magamistuba: sensorSeries(uhoo, 'Magamistuba temp', 'Magamistuba RH'),
  };
}

// ---- Now cards --------------------------------------------------------------------------------------------

interface NowCard {
  id: SensorId;
  name: string;
  src: string;
  color: string;
  now: number;
  trend?: { text: string; color: string };
  min?: number; // today, from local midnight
  max?: number;
  rh?: number; // %, latest (433 MHz sensors with humidity)
  sub?: string;
  spark?: Point[];
}

function trendOf(s: SensorSeries | undefined): NowCard['trend'] {
  const d = change(s?.temp, H, (s?.stepMs ?? 0) * 1.5 + 60_000);
  if (d === undefined) {
    return undefined;
  }
  return Math.abs(d) < 0.05
    ? { text: '→ steady', color: MUTED }
    : { text: `${d > 0 ? '↑' : '↓'} ${Math.abs(d).toFixed(1)}°`, color: d > 0 ? UP : DOWN };
}

function nowCards(day: RangeData, plan: EnergyPlanSnapshot | undefined, now: number): NowCard[] {
  const midnight = localMidnight(now);
  const cards: NowCard[] = [];
  for (const s of SENSORS) {
    const series = day[s.id];
    const today = rangeStats(series?.temp, midnight, now);
    const card = { id: s.id, name: s.name, src: s.src, color: s.color, spark: series?.temp };
    if (s.uhoo) {
      const room: EnergyAirQualityRoom | undefined = plan?.airQuality?.rooms.find((r) => r.name === s.id);
      const l = room?.latest;
      if (l?.temperatureC === undefined) {
        continue;
      }
      const parts = [
        l.humidity !== undefined ? `${Math.round(l.humidity)}% RH` : undefined,
        l.co2 !== undefined ? `CO₂ ${Math.round(l.co2)}` : undefined,
      ].filter(Boolean);
      cards.push({ ...card, now: l.temperatureC, trend: trendOf(series), sub: parts.join(' · ') || undefined });
      continue;
    }
    const last = latest(series?.temp);
    if (!last) {
      continue;
    }
    const rh = latest(series?.rh);
    cards.push({
      ...card,
      now: last[1],
      trend: trendOf(series),
      min: today?.min[1],
      max: today?.max[1],
      rh: rh?.[1],
      sub:
        s.id === 'tuba'
          ? rh && `${Math.round(rh[1])}% RH`
          : today && `today ${num1(today.min[1])}…${num1(today.max[1])}°`,
    });
  }
  return cards;
}

const cardLabel = (c: NowCard, size: LayoutSize) => (
  <span
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      color: SECONDARY,
      whiteSpace: 'nowrap',
      minWidth: 0,
    }}
  >
    <Dot color={c.color} />
    {size === 'phone' && c.id === 'outside' ? (
      `${c.name} · ${c.src}`
    ) : (
      <>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: FAINT }}>{c.src}</span>
      </>
    )}
  </span>
);

// Phone: Väljas as a wide card.
function HeroCard(props: { c: NowCard }) {
  const { c } = props;
  return (
    <Card
      label={`${c.name} now`}
      raised
      style={{
        padding: '12px 14px',
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: 14,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {cardLabel(c, 'phone')}
        <span className="num" style={{ fontSize: 34, fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.02em' }}>
          {num1(c.now)}°
        </span>
        {c.trend && (
          <span className="num" style={{ fontSize: 12, color: SECONDARY }}>
            <b style={{ color: c.trend.color }}>{c.trend.text}</b>
            {c.trend.text.startsWith('→') ? '' : ' last hour'}
          </span>
        )}
        {c.rh !== undefined && (
          <span className="num" style={{ fontSize: 12, color: SECONDARY }}>
            {Math.round(c.rh)}% RH
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div
          className="num"
          style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: SECONDARY }}
        >
          {c.min !== undefined && c.max !== undefined ? (
            <span>
              Today <b style={{ color: '#2a78d6' }}>{num1(c.min)}°</b> ·{' '}
              <b style={{ color: '#c2410c' }}>{num1(c.max)}°</b>
            </span>
          ) : (
            <span />
          )}
          <span>24 h</span>
        </div>
        {c.spark && <Sparkline points={c.spark} color={c.color} height={52} area zeroLine />}
      </div>
    </Card>
  );
}

function Tile(props: { c: NowCard; size: LayoutSize; tinted?: boolean }) {
  const { c, size } = props;
  const phone = size === 'phone';
  const outside = c.id === 'outside';
  return (
    <Card
      label={`${c.name} now`}
      radius={16}
      style={{
        padding: phone ? '10px 12px 8px' : '12px 12px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
        ...(props.tinted ? { background: '#eef4fc', boxShadow: '0 0 0 1px rgba(42,120,214,0.18)' } : {}),
      }}
    >
      {cardLabel(c, size)}
      <span className="num" style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <b style={{ fontSize: phone ? 22 : 28, fontWeight: 600, letterSpacing: phone ? '-0.01em' : '-0.02em' }}>
          {num1(c.now)}°
        </b>
        {c.trend && <span style={{ fontSize: 11, fontWeight: 600, color: c.trend.color }}>{c.trend.text}</span>}
      </span>
      <span
        className="num"
        style={{
          fontSize: 11,
          color: SECONDARY,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minHeight: 14,
        }}
      >
        {outside && c.min !== undefined && c.max !== undefined ? (
          <>
            today <b style={{ color: DOWN }}>{num1(c.min)}</b>…<b style={{ color: UP }}>{num1(c.max)}°</b>
          </>
        ) : (
          c.sub
        )}
      </span>
      {outside && c.rh !== undefined && (
        <span className="num" style={{ fontSize: 11, color: SECONDARY }}>
          {Math.round(c.rh)}% RH
        </span>
      )}
      {c.spark && (
        // marginTop auto: sparklines line up at the bottom when a tile has an extra line
        <Sparkline
          points={c.spark}
          color={c.color}
          height={phone ? 26 : 30}
          area={outside}
          zeroLine={outside}
          style={{ marginTop: 'auto', paddingTop: phone ? 2 : 4 }}
        />
      )}
    </Card>
  );
}

// ---- History ----------------------------------------------------------------------------------------------

// The humidity pane is taller with Väljas in it: outdoor humidity (≈ 60–100 %) stretches the axis that the
// indoor lines (≈ 30–45 %) share.
const PANES = (hasRh: boolean, outdoorRh: boolean): HistoryPane[] => [
  {
    key: 'temp',
    height: { phone: 190, tablet: 300, web: 320 },
    axis: {
      ...fittedAxis(0),
      labels: {
        formatter() {
          return degrees(this.value as number);
        },
      },
      plotLines: [{ value: 0, color: ZERO_LINE, dashStyle: 'Dash', width: 1, zIndex: 3 }],
    },
  },
  ...(hasRh
    ? [
        {
          key: 'rh',
          height: outdoorRh ? { phone: 76, tablet: 110, web: 120 } : { phone: 56, tablet: 80, web: 90 },
          title: 'Humidity %',
          axis: fittedAxis(5),
        },
      ]
    : []),
];

function StatsTable(props: {
  rows: { id: SensorId; name: string; color: string; stats: NonNullable<ReturnType<typeof rangeStats>> }[];
  rangeName: string;
  long: boolean;
  size: LayoutSize;
}) {
  const phone = props.size === 'phone';
  // (On the web the name column stops growing, so the numbers stay next to the names.)
  const cols = phone
    ? 'minmax(0, 1fr) repeat(3, 52px)'
    : `minmax(0, ${props.size === 'web' ? '320px' : '1fr'}) 64px 64px 150px 64px 150px`;
  const when = (t: number) => weekdayTime(t, props.long);
  const right: React.CSSProperties = { textAlign: 'right' };
  const faint: React.CSSProperties = { fontSize: 11, color: FAINT, whiteSpace: 'nowrap' };
  return (
    <div
      className="num"
      role="table"
      aria-label="Range statistics"
      style={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: phone ? 12 : 13,
        borderTop: `1px solid ${HAIRLINE}`,
        paddingTop: 6,
      }}
    >
      <div
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: phone ? 8 : 10,
          fontSize: 11,
          color: MUTED,
          padding: '2px 0',
        }}
      >
        <span role="columnheader">{props.rangeName}</span>
        {phone ? (
          <>
            <span role="columnheader" style={right}>
              Min
            </span>
            <span role="columnheader" style={right}>
              Avg
            </span>
            <span role="columnheader" style={right}>
              Max
            </span>
          </>
        ) : (
          <>
            <span role="columnheader" style={right}>
              Now
            </span>
            <span role="columnheader" style={right}>
              Min
            </span>
            <span role="columnheader" />
            <span role="columnheader" style={right}>
              Max
            </span>
            <span role="columnheader" />
          </>
        )}
      </div>
      {props.rows.map(({ id, name, color, stats }) => (
        <div
          key={id}
          role="row"
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            gap: phone ? 8 : 10,
            padding: phone ? '5px 0' : '6px 0',
            borderTop: `1px solid ${ROW_LINE}`,
            alignItems: 'baseline',
          }}
        >
          <span
            role="cell"
            style={{ display: 'flex', alignItems: 'center', gap: phone ? 6 : 8, color: INK_SOFT, minWidth: 0 }}
          >
            <LineMark color={color} width={phone ? 10 : 12} />
            {name}
            {!phone && <span style={{ fontSize: 11, color: FAINT }}>avg {num1(stats.avg)}°</span>}
          </span>
          {phone ? (
            <>
              <span role="cell" title={`at ${when(stats.min[0])}`} style={{ ...right, color: DOWN }}>
                {num1(stats.min[1])}°
              </span>
              <span role="cell" style={right}>
                {num1(stats.avg)}°
              </span>
              <span role="cell" title={`at ${when(stats.max[0])}`} style={{ ...right, color: UP }}>
                {num1(stats.max[1])}°
              </span>
            </>
          ) : (
            <>
              <span role="cell" style={{ ...right, fontWeight: 600 }}>
                {num1(stats.last[1])}°
              </span>
              <span role="cell" style={{ ...right, color: DOWN }}>
                {num1(stats.min[1])}°
              </span>
              <span role="cell" style={faint}>
                {when(stats.min[0])}
              </span>
              <span role="cell" style={{ ...right, color: UP }}>
                {num1(stats.max[1])}°
              </span>
              <span role="cell" style={faint}>
                {when(stats.max[0])}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- Tab --------------------------------------------------------------------------------------------------

export const TempTab: React.FC = (): React.ReactElement => {
  const data = useHomeData();
  const size = layoutFor(useViewportWidth());
  const [now, setNow] = useState(data.now);
  const [range, setRange] = useState<HistoryRange>('24h');
  const [day, setDay] = useState<RangeData | undefined>(undefined); // 24 h: cards, sparklines, status
  const [ranged, setRanged] = useState<{ range: HistoryRange; data: RangeData } | undefined>(undefined);
  const [plan, setPlan] = useState<EnergyPlanSnapshot | undefined>(undefined);
  const [hidden, setHidden] = useState<Set<SensorId>>(new Set());

  // Exports arrive every 5 minutes; the uHoo "now" with the plan every minute.
  useInterval(async () => {
    setNow(data.now());
    const d = await loadRange(data, '24h');
    setDay(d);
    if (range === '24h') {
      setRanged({ range, data: d });
    } else {
      setRanged({ range, data: await loadRange(data, range) });
    }
  }, 5 * 60_000);
  useInterval(async () => {
    setNow(data.now());
    setPlan(await data.plan());
  }, 60_000);
  useEffect(() => {
    if (ranged && ranged.range !== range) {
      let cancelled = false;
      (range === '24h' && day ? Promise.resolve(day) : loadRange(data, range)).then((d) => {
        if (!cancelled) setRanged({ range, data: d });
      });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [range]);

  // "● sensors 2 min ago": the newest 433 MHz reading (exports every 5 min).
  const newest = day
    ? Math.max(0, ...(['outside', 'kuur', 'tuba'] as SensorId[]).map((id) => latest(day[id]?.temp)?.[0] ?? 0))
    : 0;
  const stale = newest > 0 && now - newest > SENSORS_STALE_AFTER_MS;
  const age = newest > 0 ? formatAge(newest, now) : '';
  const status = useMemo(() => {
    if (newest === 0) {
      return null;
    }
    const lead = `sensors ${stale ? 'stale · ' : ''}${age}`;
    const sources = plan?.airQuality ? '433 MHz · uHoo' : '433 MHz';
    return (
      <HeaderStatus
        size={size}
        stale={stale}
        text={{
          phone: lead,
          tablet: [capitalize(`${lead} · ${hhmm(newest)}`), sources],
          web: [`${lead} (${hhmm(newest)})`, sources],
        }}
      />
    );
  }, [size, stale, age, newest, plan?.airQuality !== undefined]);
  useHeaderStatus(status);

  const cards = useMemo(() => (day ? nowCards(day, plan, now) : []), [day, plan, now]);
  const rd = ranged?.data;
  const info = rangeInfo(ranged?.range ?? range);
  const win: [number, number] = [now - info.hours * H, now];
  const available = SENSORS.filter((s) => rd?.[s.id]);
  const hasRh = available.some((s) => rd?.[s.id]?.rh);
  const outdoorRh = !!rd?.outside?.rh;
  const panes = useMemo(() => PANES(hasRh, outdoorRh), [hasRh, outdoorRh]);
  const series = useMemo(() => {
    const out: HistorySeries[] = [];
    for (const s of available) {
      const visible = !hidden.has(s.id);
      out.push({ id: `t-${s.id}`, pane: 'temp', data: rd![s.id]!.temp!, color: s.color, visible });
      if (hasRh && rd![s.id]!.rh) {
        out.push({ id: `h-${s.id}`, pane: 'rh', data: rd![s.id]!.rh!, color: s.color, visible, lineWidth: 1 });
      }
    }
    return out;
  }, [rd, hidden, hasRh]);
  const minStep = Math.min(...available.map((s) => rd![s.id]!.stepMs), 3_600_000);

  const page = pageStyle(size);
  if (!day || !ranged) {
    return (
      <div style={page}>
        <Alert variant="info" className="mb-0">
          Loading temperatures…
        </Alert>
      </div>
    );
  }
  if (cards.length === 0 && available.length === 0) {
    return (
      <div style={page}>
        <Alert variant="info" className="mb-0">
          No temperature data yet: the Pi uploads the sensor exports every 5 minutes (add ?mock=temp to the URL for
          sample data).
        </Alert>
      </div>
    );
  }

  const phone = size === 'phone';
  const hero = cards.find((c) => c.id === 'outside');
  const chips = (t: number | null) => (
    <div role="group" aria-label="Sensors (tap to show or hide)" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {available.map((s) => {
        const sd = rd![s.id]!;
        const tol = sd.stepMs * 1.5;
        const at = (pts: Point[] | undefined) => (t === null ? latest(pts)?.[1] : valueAt(pts, t, tol));
        const temp = at(sd.temp);
        const rh = at(sd.rh);
        return (
          <ToggleChip
            key={s.id}
            name={s.name}
            color={s.color}
            on={!hidden.has(s.id)}
            size={size}
            value={`${temp === undefined ? '—' : `${num1(temp)}°`}${rh === undefined ? '' : ` ${Math.round(rh)}%`}`}
            onToggle={() => {
              const next = new Set(hidden);
              if (!next.delete(s.id)) next.add(s.id);
              setHidden(next);
            }}
          />
        );
      })}
    </div>
  );
  const statsRows = available
    .filter((s) => !hidden.has(s.id))
    .map((s) => ({ id: s.id, name: s.name, color: s.color, stats: rangeStats(rd![s.id]!.temp, win[0], win[1])! }))
    .filter((r) => r.stats);

  return (
    <div style={page}>
      {phone ? (
        <>
          {hero && <HeroCard c={hero} />}
          <div
            aria-label="Indoor and shed now"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}
          >
            {cards
              .filter((c) => c !== hero)
              .map((c) => (
                <Tile key={c.id} c={c} size={size} />
              ))}
          </div>
        </>
      ) : (
        <div
          aria-label="Now"
          style={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))`, gap: 10 }}
        >
          {cards.map((c) => (
            <Tile key={c.id} c={c} size={size} tinted={c.id === 'outside'} />
          ))}
        </div>
      )}

      {available.length > 0 && (
        <HistoryCard
          size={size}
          now={now}
          range={range}
          onRange={setRange}
          win={win}
          panes={panes}
          series={series}
          minRangeMs={Math.max(15 * 60_000, 4 * minStep)}
          chips={chips}
        >
          {statsRows.length > 0 && (
            <StatsTable rows={statsRows} rangeName={info.name} long={longRange(win[1] - win[0])} size={size} />
          )}
          {info.hours > 720 && cards.some((c) => c.src === 'uHoo' && !rd?.[c.id]) && (
            // The uHoo export stops at 1m (pi/energy/tools/export_history.ts).
            <span style={{ fontSize: 11, color: MUTED }}>Kabinet, Magamistuba: history up to 1 month</span>
          )}
        </HistoryCard>
      )}

      <p className="num" style={{ margin: 0, fontSize: 11, color: MUTED }}>
        Väljas, Kuur, Tuba: 433 MHz sensors · Kabinet, Magamistuba: uHoo · dashed line = 0 °C
      </p>
    </div>
  );
};
